import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Clock3, Database, Mail, ShieldCheck, UserMinus, Users } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import {
  ActionButton,
  CustomerInput,
  CustomerPanel,
  CustomerSelect,
  EmptyState,
  MetricCard,
  StatusBadge,
  TableWrapper,
} from "../components/customer/CustomerUI";
import api, { formatApiError } from "../lib/api";
import { LoadingState, ErrorState } from "../components/LoadingState";

export const ROLE_DESCRIPTIONS = {
  owner:
    "Owners manage invitations, member removal, role changes, CRM access, and plan and billing changes.",
  admin:
    "Organization admins can invite and remove members, cancel invitations, and manage CRM access. They cannot appoint admins, change roles, or manage billing.",
  member:
    "Members can use only the CRM connections assigned to them. They cannot invite or remove teammates, change roles, or manage billing.",
};

export const ONE_ORGANIZATION_NOTICE =
  "Each CleanIQ login can belong to only one organization. If the recipient already belongs to another CleanIQ organization or has CRM data in a personal workspace, they must use a separate email address.";

export async function acceptInvitationFromUrl({
  searchParams,
  setSearchParams,
  client = api,
}) {
  const token = searchParams.get("invite");
  if (!token) return false;

  const scrubbedParams = new URLSearchParams(searchParams);
  scrubbedParams.delete("invite");
  setSearchParams(scrubbedParams, { replace: true });
  await client.post("/organization/invitations/accept", { token });
  return true;
}

export function confirmInvitationCancellation(email, confirmAction = window.confirm) {
  return confirmAction(
    `Cancel the invitation for ${email}? Their one-time link will stop working and the reserved seat will be released.`,
  );
}

export function canManageOrganizationBilling(role) {
  return role === "owner";
}

function roleLabel(role) {
  if (role === "admin") return "Organization admin";
  return role === "owner" ? "Owner" : "Member";
}

export function SeatChangeHistory({ events, unavailable = false }) {
  return <CustomerPanel
    title="Team access history"
    description="Plan-driven membership and invitation changes remain recorded here after email delivery."
    data-testid="team-seat-history"
  >
    {unavailable ? <EmptyState
      icon={Clock3}
      title="Team access history unavailable"
      description="CleanIQ could not load the durable team-change record. Refresh to try again."
    /> : events.length === 0 ? <EmptyState
      icon={Clock3}
      title="No plan-driven team changes"
      description="Seat reductions that move members or cancel invitations will appear here."
    /> : <div className="divide-y divide-slate-100">
      {events.map((event) => <article key={event.id} className="min-w-0 space-y-ui-3 px-ui-4 py-ui-4 sm:px-ui-5">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-ui-2">
          <div className="font-semibold text-slate-900">Team access changed</div>
          <StatusBadge tone="warning">
            {event.effective_plan ? `${event.effective_plan} plan` : "Plan change"}
          </StatusBadge>
        </div>
        <p className="text-sm text-slate-600">
          Seat limit: {event.seat_limit ?? "Unavailable"} · {event.occurred_at
            ? new Date(event.occurred_at).toLocaleString()
            : "Date unavailable"}
        </p>
        {event.removed_members.length > 0 && <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Moved to personal workspaces</div>
          <ul className="mt-ui-1 space-y-ui-1 text-sm text-slate-700">
            {event.removed_members.map((member, index) => <li key={`${member.email || member.name}-${index}`} className="break-words">
              {member.name || member.email || "Team member"}{member.name && member.email ? ` — ${member.email}` : ""}
            </li>)}
          </ul>
        </div>}
        {event.cancelled_invitations.length > 0 && <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Invitations cancelled</div>
          <ul className="mt-ui-1 space-y-ui-1 text-sm text-slate-700">
            {event.cancelled_invitations.map((invitation, index) => <li key={`${invitation.email}-${index}`} className="break-all">
              {invitation.email || "Invitation recipient unavailable"}
            </li>)}
          </ul>
        </div>}
      </article>)}
    </div>}
  </CustomerPanel>;
}

export default function Team() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [organization, setOrganization] = useState(null);
  const [connections, setConnections] = useState([]);
  const [seatHistory, setSeatHistory] = useState([]);
  const [seatHistoryUnavailable, setSeatHistoryUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [invite, setInvite] = useState({ email: "", role: "member" });

  const load = useCallback(async () => {
    try {
      const [{ data: organizationData }, { data: connectionData }] = await Promise.all([
        api.get("/organization"),
        api.get("/connections"),
      ]);
      setOrganization(organizationData);
      setConnections(connectionData);
      if (["owner", "admin"].includes(organizationData.current_user_role)) {
        try {
          const { data: historyData } = await api.get("/organization/seat-history");
          setSeatHistory(historyData);
          setSeatHistoryUnavailable(false);
        } catch (_historyError) {
          setSeatHistory([]);
          setSeatHistoryUnavailable(true);
        }
      } else {
        setSeatHistory([]);
        setSeatHistoryUnavailable(false);
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!searchParams.get("invite")) return;
    setBusy("accept");
    acceptInvitationFromUrl({ searchParams, setSearchParams })
      .then(() => {
        toast.success("You joined the organization.");
        return load();
      })
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => setBusy(""));
  }, [searchParams, setSearchParams, load]);

  const sendInvite = async (event) => {
    event.preventDefault();
    setBusy("invite");
    try {
      await api.post("/organization/invitations", invite);
      setInvite({ email: "", role: "member" });
      toast.success("Invitation sent.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const updateRole = async (membershipId, role) => {
    setBusy(membershipId);
    try {
      await api.patch(`/organization/members/${membershipId}`, { role });
      toast.success("Member role updated.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const removeMember = async (membershipId, name) => {
    if (!window.confirm(`Remove ${name} from this organization?`)) return;
    setBusy(membershipId);
    try {
      await api.delete(`/organization/members/${membershipId}`);
      toast.success("Member removed.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const updateAssignments = async (connection, userId, checked) => {
    const current = new Set(connection.assigned_user_ids || []);
    if (checked) current.add(userId); else current.delete(userId);
    setBusy(`assignment-${connection.id}`);
    try {
      const { data } = await api.patch(
        `/connections/${connection.id}/assignments`,
        { user_ids: Array.from(current) },
      );
      setConnections((items) => items.map((item) => item.id === data.id ? data : item));
      toast.success("CRM access updated.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const cancelInvitation = async (invitation) => {
    if (!confirmInvitationCancellation(invitation.email)) return;
    setBusy(invitation.id);
    try {
      await api.delete(`/organization/invitations/${invitation.id}`);
      toast.success("Invitation cancelled.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  if (loading) return <AppShell><LoadingState label="Loading team" /></AppShell>;
  if (!organization) return <AppShell><ErrorState message="Team information is unavailable." onRetry={load} /></AppShell>;

  const canManage = ["owner", "admin"].includes(organization.current_user_role);
  const isOwner = organization.current_user_role === "owner";
  const canManageBilling = canManageOrganizationBilling(organization.current_user_role);
  const atCapacity = organization.seats_reserved >= organization.seat_limit;

  return <AppShell>
    <div className="min-w-0 space-y-ui-6" data-testid="team-page">
      <div className="min-w-0">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-blue-600">Organization access</div>
        <h1 className="mt-ui-2 break-words font-heading text-3xl font-bold tracking-tight text-slate-950">{organization.name}</h1>
        <p className="mt-ui-2 text-sm text-slate-600">Give each teammate their own secure login and traceable audit identity.</p>
      </div>

      <div className="grid min-w-0 gap-ui-4 md:grid-cols-3">
        <MetricCard
          data-testid="team-seat-metric"
          icon={Users}
          value={`${organization.seats_used} / ${organization.seat_limit}`}
          label="CleanIQ seats used"
          detail={canManageBilling ? "Open plans and billing" : "Only the organization owner can change the plan."}
          onClick={canManageBilling ? () => navigate("/billing") : undefined}
        />
        <MetricCard
          icon={ShieldCheck}
          value={roleLabel(organization.current_user_role)}
          label="Your organization role"
          detail={ROLE_DESCRIPTIONS[organization.current_user_role]}
          valueClassName="text-xl"
        />
        <MetricCard
          icon={Mail}
          value={organization.invitations.length}
          label="Pending invitations"
          detail="Pending invitations reserve seats until accepted, cancelled, or expired."
        />
      </div>

      {canManage && organization.seat_limit > 1 && <CustomerPanel
        title="Invite a teammate"
        description="Pending invitations reserve a seat. Shared logins are not permitted."
      >
        <form onSubmit={sendInvite} className="p-ui-4 sm:p-ui-5">
          <p id="team-organization-rule" className="mb-ui-4 text-sm leading-6 text-slate-600">
            {ONE_ORGANIZATION_NOTICE}
          </p>
          <div className="grid min-w-0 gap-ui-3 md:grid-cols-[minmax(0,1fr)_minmax(12rem,15rem)_auto]">
            <div className="min-w-0">
              <label htmlFor="team-invite-email" className="mb-ui-1 block text-sm font-semibold text-slate-700">Email address</label>
              <CustomerInput
                id="team-invite-email"
                data-testid="team-invite-email"
                type="email"
                required
                value={invite.email}
                onChange={(event) => setInvite({ ...invite, email: event.target.value })}
                placeholder="teammate@company.com"
                aria-describedby="team-organization-rule"
              />
            </div>
            <div className="min-w-0">
              <label htmlFor="team-invite-role" className="mb-ui-1 block text-sm font-semibold text-slate-700">Role</label>
              <CustomerSelect
                id="team-invite-role"
                data-testid="team-invite-role"
                value={invite.role}
                onChange={(event) => setInvite({ ...invite, role: event.target.value })}
                aria-describedby="team-invite-role-help team-organization-rule"
              >
                <option value="member">Member</option>
                {isOwner && <option value="admin">Organization admin</option>}
              </CustomerSelect>
            </div>
            <ActionButton
              data-testid="team-invite-submit"
              type="submit"
              disabled={busy === "invite" || atCapacity}
              className="self-end md:whitespace-nowrap"
            >
              {busy === "invite" ? "Sending…" : "Send invitation"}
            </ActionButton>
          </div>
          <p id="team-invite-role-help" className="mt-ui-3 text-sm leading-6 text-slate-500">
            {ROLE_DESCRIPTIONS[invite.role]}
          </p>
          {atCapacity && <div className="mt-ui-4 flex flex-col gap-ui-3 rounded-customer border border-amber-200 bg-amber-50 p-ui-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
            <p>
              {canManageBilling
                ? "All seats are assigned or reserved. Upgrade the plan or cancel an invitation."
                : "All seats are assigned or reserved. Ask the organization owner to upgrade the plan, or cancel an invitation."}
            </p>
            {canManageBilling && <ActionButton variant="secondary" onClick={() => navigate("/billing")} className="shrink-0">
              Upgrade plan
            </ActionButton>}
          </div>}
        </form>
      </CustomerPanel>}

      {canManage && organization.seat_limit === 1 && <CustomerPanel
        data-testid="team-upgrade-message"
        title="Team access starts with Growth"
        description="Free and Starter workspaces include one secure user. Growth includes up to three total users."
      >
        <div className="p-ui-4 sm:p-ui-5">
          {canManageBilling ? <ActionButton onClick={() => navigate("/billing")}>View team plans</ActionButton> :
            <p className="text-sm text-slate-600">Ask the organization owner to review team plans and billing.</p>}
        </div>
      </CustomerPanel>}

      <TableWrapper title="Members" description="Roles determine organization administration and CRM visibility.">
        <div className="divide-y divide-slate-100">
          {organization.members.map((member) => {
            const roleHelpId = `member-role-help-${member.membership_id}`;
            return <div key={member.membership_id}
              className="flex min-w-0 flex-col gap-ui-3 px-ui-4 py-ui-4 sm:flex-row sm:items-start sm:justify-between sm:px-ui-5">
              <div className="min-w-0">
                <div className="break-words font-semibold text-slate-900">{member.name}</div>
                <div className="break-all text-sm text-slate-500">{member.email}</div>
              </div>
              <div className="flex min-w-0 flex-col gap-ui-2 sm:max-w-md sm:items-end">
                <div className="flex min-w-0 flex-wrap items-center gap-ui-2">
                  {isOwner && member.organization_role !== "owner" ? <CustomerSelect
                    aria-label={`Role for ${member.name}`}
                    aria-describedby={roleHelpId}
                    value={member.organization_role}
                    disabled={busy === member.membership_id}
                    onChange={(event) => updateRole(member.membership_id, event.target.value)}
                    className="w-auto min-w-[12rem]"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Organization admin</option>
                  </CustomerSelect> : <StatusBadge tone={member.organization_role === "owner" ? "info" : "neutral"}>
                    {roleLabel(member.organization_role)}
                  </StatusBadge>}
                  {canManage && member.organization_role !== "owner" && <ActionButton
                    aria-label={`Remove ${member.name}`}
                    title={`Remove ${member.name}`}
                    variant="destructive"
                    size="icon"
                    disabled={busy === member.membership_id}
                    onClick={() => removeMember(member.membership_id, member.name)}
                  >
                    <UserMinus aria-hidden="true" />
                  </ActionButton>}
                </div>
                <p id={roleHelpId} className="text-xs leading-5 text-slate-500 sm:text-right">
                  {ROLE_DESCRIPTIONS[member.organization_role]}
                </p>
              </div>
            </div>;
          })}
        </div>
      </TableWrapper>

      {canManage && <TableWrapper
        title="CRM access"
        description="Owners and organization admins can access every CRM. Members see only the CRM connections assigned here."
        data-testid="crm-access"
      >
        {connections.length === 0 ? <EmptyState
          icon={Database}
          title="No CRM connections yet"
          description="Connect a CRM before assigning private CRM access to members."
        /> : <div className="divide-y divide-slate-100">
          {connections.map((connection) => <div key={connection.id} className="min-w-0 px-ui-4 py-ui-4 sm:px-ui-5">
            <div className="flex min-w-0 flex-wrap items-center gap-ui-2">
              <div className="min-w-0 break-words font-semibold text-slate-900">{connection.label}</div>
              {!connection.plan_access_enabled && <StatusBadge tone="warning">Plan locked</StatusBadge>}
            </div>
            <div className="break-words text-xs text-slate-500">{connection.connector_name}</div>
            <div className="mt-ui-3 flex min-w-0 flex-wrap gap-ui-2">
              {organization.members.filter((member) => member.organization_role === "member").map((member) => {
                const checked = (connection.assigned_user_ids || []).includes(member.user_id);
                const creator = connection.created_by_user_id === member.user_id;
                return <label key={member.user_id} className="flex min-h-touch min-w-0 max-w-full cursor-pointer items-center gap-ui-2 rounded-customer border border-slate-200 px-ui-3 py-ui-2 text-sm focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={creator || busy === `assignment-${connection.id}`}
                    onChange={(event) => updateAssignments(connection, member.user_id, event.target.checked)}
                    className="h-4 w-4 shrink-0 accent-blue-600"
                    aria-label={`Assign ${member.name} to ${connection.label}`}
                  />
                  <span className="min-w-0 break-words">{member.name}</span>
                  {creator && <span className="shrink-0 text-xs text-slate-400">(connected)</span>}
                </label>;
              })}
              {organization.members.every((member) => member.organization_role !== "member") && <p className="text-sm text-slate-500">
                {organization.seat_limit > 1
                  ? "Invite a member to assign private CRM access."
                  : "Upgrade to Growth to assign private CRM access to team members."}
              </p>}
            </div>
          </div>)}
        </div>}
      </TableWrapper>}

      {canManage && <TableWrapper
        title="Pending invitations"
        description="Pending links reserve a seat until they are accepted, cancelled, or expire."
      >
        {organization.invitations.length === 0 ? <EmptyState
          icon={Mail}
          title="No pending invitations"
          description="New invitations will appear here until the recipient accepts or the link expires."
        /> : <div className="divide-y divide-slate-100">
          {organization.invitations.map((item) => <div key={item.id} className="flex min-w-0 flex-col gap-ui-3 px-ui-4 py-ui-4 sm:flex-row sm:items-center sm:justify-between sm:px-ui-5">
            <div className="min-w-0">
              <div className="break-all font-semibold text-slate-900">{item.email}</div>
              <div className="mt-ui-1 flex flex-wrap items-center gap-ui-2 text-xs text-slate-500">
                <StatusBadge tone="warning">{roleLabel(item.role)}</StatusBadge>
                <span>Expires {new Date(item.expires_at).toLocaleDateString()}</span>
              </div>
            </div>
            <ActionButton
              aria-label={`Cancel invitation for ${item.email}`}
              variant="destructive"
              disabled={busy === item.id}
              onClick={() => cancelInvitation(item)}
              className="shrink-0 sm:self-center"
            >
              Cancel invitation
            </ActionButton>
          </div>)}
        </div>}
      </TableWrapper>}

      {canManage && <SeatChangeHistory
        events={seatHistory}
        unavailable={seatHistoryUnavailable}
      />}
    </div>
  </AppShell>;
}
